import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import useScrollToTop from '../hooks/useScrollToTop';
import SEOHead from '../components/seo/SEOHead';
import blogPosts from '../data/blog-posts.json';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug && p.published !== false);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  
  useScrollToTop();

  useEffect(() => {
    if (post && post.contentFile) {
      fetch(post.contentFile)
        .then(response => response.text())
        .then(text => {
          setContent(text);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading content:', error);
          setLoading(false);
        });
    } else if (post && post.content) {
      // Fallback for posts that still use inline content
      setContent(post.content);
      setLoading(false);
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <SEOHead 
          title="Blog Post Not Found"
          description="The blog post you're looking for doesn't exist."
          type="website"
          url="https://customsync.app/blog/not-found"
        />
        <Header />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
            <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
            <Link 
              to="/blog"
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SEOHead 
          title={post.title}
          description={post.excerpt}
          type="article"
          url={`https://customsync.app/blog/${post.slug}`}
        />
        <Header />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category}, ${post.title.toLowerCase()}, CustomSync, Amazon customization, e-commerce automation`}
        type="article"
        url={`https://customsync.app/blog/${post.slug}`}
        publishedTime={post.date}
        section={post.category}
        tags={[post.category, 'CustomSync', 'Amazon customization', 'e-commerce automation']}
        image={post.image}
      />
      <Header />
      
      <main className="pt-20">
        {/* Article Header */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center text-sm text-gray-500 mb-4">
                <time dateTime={post.date}>{post.date}</time>
                {post.category && (
                  <>
                    <span className="mx-2">•</span>
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {post.category}
                    </span>
                  </>
                )}
              </div>
              <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl mb-6">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {post.excerpt}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {post.image && (
            <div className="mb-8">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-64 md:h-96 object-contain rounded-lg"
              />
            </div>
          )}
          
          {/* Enhanced Blog Content Styling */}
          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-h1:text-3xl prose-h1:mb-8 prose-h1:mt-12 prose-h2:text-2xl prose-h2:mb-6 prose-h2:mt-10 prose-h3:text-xl prose-h3:mb-4 prose-h3:mt-8 prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-strong:text-gray-900 prose-strong:font-semibold prose-ul:my-6 prose-ol:my-6 prose-li:mb-2 prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-600 prose-a:text-blue-600 prose-a:font-medium prose-a:underline prose-a:hover:text-blue-800 prose-img:shadow-none [&_figure]:my-6 [&_figure]:flex [&_figure]:flex-col [&_figure]:items-center [&_figure_img]:max-w-[650px] [&_figure_img]:w-full [&_figure_img]:h-auto [&_figure_img]:rounded-lg [&_figure_img]:border [&_figure_img]:border-gray-300 [&_figure_img]:!shadow-none [&_figcaption]:mt-2 [&_figcaption]:text-sm [&_figcaption]:text-gray-600 [&_figcaption]:italic [&_figcaption]:text-center">
            <ReactMarkdown 
              components={{
                a: ({ href, children, ...props }) => {
                  // Check if it's an external link
                  const isExternal = href && (href.startsWith('http') || href.startsWith('//'));
                  return (
                    <a 
                      href={href} 
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
                img: ({ src, alt, title, ...props }) => {
                  return (
                    <figure className="my-6 flex flex-col items-center">
                      <img 
                        src={src} 
                        alt={alt}
                        className="max-w-[650px] w-full h-auto rounded-lg border border-gray-300 !shadow-none"
                        style={{ boxShadow: 'none' }}
                        {...props}
                      />
                      {title && (
                        <figcaption className="mt-2 text-sm text-gray-600 italic text-center">
                          {title}
                        </figcaption>
                      )}
                    </figure>
                  );
                }
              }}
            >
              {content}
            </ReactMarkdown>
          </div>

          {/* Back to Blog */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <Link 
              to="/blog"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blog
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
